/**
 * Disqualify Report Page
 *
 * Surfaces disqualified orders with trend, reason, product, and funnel-stage breakdowns.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { format, parse } from 'date-fns'
import { Card, CardHeader, CardTitle, CardContent, CardDescription, Button } from '../components/ui'
import { DateRangePicker } from '../components/DateRangePicker'
import { disqualifiesAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import DataFreshnessWarning from '../components/DataFreshnessWarning'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts'
import {
  AlertCircle, RefreshCw, Loader2, ShieldX, TrendingDown, Package, Users,
} from 'lucide-react'

const PIE_COLORS = ['#EF4444', '#F97316', '#F59E0B', '#3B82F6', '#8B5CF6', '#6B7280', '#22C55E', '#14B8A6', '#EC4899', '#A855F7']

const getDefaultDateRange = () => {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - 29)
  return { from: start, to: end }
}

function formatNumber(num) {
  return new Intl.NumberFormat('en-US').format(num)
}

function formatCurrency(cents) {
  if (!cents) return '$0'
  const dollars = cents / 100
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(dollars)
}

function formatStatus(status) {
  return (status || 'UNKNOWN').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

function SummaryCard({ title, value, subtitle, icon: Icon, loading }) {
  return (
    <Card>
      <CardContent className="p-6">
        {loading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-1/2" />
            <div className="h-8 bg-muted rounded w-3/4" />
            <div className="h-3 bg-muted rounded w-1/3" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">{title}</span>
              {Icon && (
                <div className="p-2 bg-destructive/10 rounded-lg">
                  <Icon className="h-4 w-4 text-destructive" />
                </div>
              )}
            </div>
            <div className="text-2xl font-bold tracking-tight">{value}</div>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-popover border border-border rounded-lg shadow-lg p-3 min-w-[140px]">
      <p className="font-medium text-sm mb-1">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center justify-between text-xs gap-4">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
            {entry.name}
          </span>
          <span className="font-medium">{entry.value}</span>
        </div>
      ))}
    </div>
  )
}

function renderPieLabel({ name, percent }) {
  if (percent < 0.05) return null
  return `${name} (${(percent * 100).toFixed(0)}%)`
}

export default function DisqualifiesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const { currentClinicId } = useAuthStore()
  const isAggregateMode = currentClinicId === null

  const dateRange = useMemo(() => {
    const s = searchParams.get('start')
    const e = searchParams.get('end')
    if (s && e) {
      try {
        const from = parse(s, 'yyyy-MM-dd', new Date())
        const to = parse(e, 'yyyy-MM-dd', new Date())
        if (!isNaN(from.getTime()) && !isNaN(to.getTime())) return { from, to }
      } catch {}
    }
    return getDefaultDateRange()
  }, [searchParams])

  const setDateRange = useCallback((r) => {
    if (r?.from && r?.to) {
      setSearchParams(prev => {
        const p = new URLSearchParams(prev)
        p.set('start', format(r.from, 'yyyy-MM-dd'))
        p.set('end', format(r.to, 'yyyy-MM-dd'))
        return p
      })
    }
  }, [setSearchParams])

  const fetchData = useCallback(async (refresh = false) => {
    if (!dateRange?.from || !dateRange?.to) return
    try {
      refresh ? setIsRefreshing(true) : setLoading(true)
      setError(null)
      const startDate = format(dateRange.from, 'yyyy-MM-dd')
      const endDate = format(dateRange.to, 'yyyy-MM-dd')
      const res = isAggregateMode
        ? await disqualifiesAPI.getReportAggregate({ startDate, endDate })
        : await disqualifiesAPI.getReport({ startDate, endDate })
      if (res.data?.success) {
        setData(res.data.data)
      } else {
        throw new Error(res.data?.error || 'Failed to load')
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || err.response?.data?.error || err.message)
    } finally {
      setLoading(false)
      setIsRefreshing(false)
    }
  }, [dateRange, isAggregateMode])

  useEffect(() => { fetchData() }, [fetchData])

  if (error && !data) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Disqualify Report</h1>
        <Card className="p-8">
          <div className="flex flex-col items-center justify-center text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-semibold mb-2">Failed to Load Report</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => fetchData()}><RefreshCw className="h-4 w-4 mr-2" />Retry</Button>
          </div>
        </Card>
      </div>
    )
  }

  const summary = data?.summary || {}
  const daily = data?.daily || []
  const byReason = data?.by_reason || []
  const byProduct = data?.by_product || []
  const byStage = data?.by_stage || []
  const byUser = data?.by_user || []
  const recent = data?.recent || []

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold truncate">
            Disqualify Report{isAggregateMode ? ' - All Brands' : ''}
          </h1>
          <p className="text-xs text-muted-foreground hidden sm:block">
            Orders disqualified in the selected period
          </p>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button variant="outline" size="icon" onClick={() => fetchData(true)} disabled={isRefreshing} className="h-8 w-8 sm:h-9 sm:w-9">
            {isRefreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard title="Total DQs" value={formatNumber(summary.total_dqs || 0)} subtitle={`of ${formatNumber(summary.total_orders || 0)} total orders`} icon={ShieldX} loading={loading} />
        <SummaryCard title="DQ Rate" value={`${summary.dq_rate || 0}%`} subtitle="of orders in period" icon={TrendingDown} loading={loading} />
        <SummaryCard title="DQs with Payment" value={formatNumber(summary.with_payment || 0)} subtitle={summary.lost_revenue ? `${formatCurrency(summary.lost_revenue)} paid` : 'No payments'} icon={Package} loading={loading} />
        <SummaryCard title="Unique Reasons" value={formatNumber(byReason.filter(r => r.name !== 'Not specified').length)} subtitle={`${byReason.find(r => r.name === 'Not specified')?.count || 0} without reason`} icon={Users} loading={loading} />
      </div>

      {/* Aggregate brand breakdown */}
      {isAggregateMode && data?.brand_breakdown?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Brand Breakdown</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 font-medium">Brand</th>
                    <th className="text-right py-2 px-2 font-medium">DQs</th>
                    <th className="text-right py-2 px-2 font-medium">Total Orders</th>
                    <th className="text-right py-2 px-2 font-medium">DQ Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.brand_breakdown.map((b, i) => (
                    <tr key={i} className="border-b border-muted/50 hover:bg-muted/50">
                      <td className="py-2 px-2 font-medium">{b.displayName}</td>
                      <td className="text-right py-2 px-2">{formatNumber(b.total_dqs)}</td>
                      <td className="text-right py-2 px-2 text-muted-foreground">{formatNumber(b.total_orders)}</td>
                      <td className={`text-right py-2 px-2 font-medium ${b.dq_rate > 5 ? 'text-red-600 dark:text-red-400' : b.dq_rate > 2 ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>
                        {b.dq_rate}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* DQ Trend Chart */}
      <Card>
        <CardHeader><CardTitle className="text-base">DQ Trend</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-[250px] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
          ) : daily.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="displayDate" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} interval={Math.max(0, Math.floor(daily.length / 8))} />
                <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" name="Disqualified" fill="#EF4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-muted-foreground">No DQs in this period</div>
          )}
        </CardContent>
      </Card>

      {/* Breakdowns: Reason + Product */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* By Reason */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">By Reason</CardTitle>
            <CardDescription>{byReason.length} unique reasons</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="animate-pulse space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-6 bg-muted rounded" />)}</div>
            ) : byReason.length > 0 ? (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {byReason.map((r, i) => (
                  <div key={i} className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-sm truncate">{r.name}</span>
                    </div>
                    <span className="text-sm font-medium shrink-0">{r.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No data</p>
            )}
          </CardContent>
        </Card>

        {/* By Product */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">By Product</CardTitle>
            <CardDescription>{byProduct.length} products</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-[200px] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : byProduct.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={byProduct} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={renderPieLabel}>
                    {byProduct.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No data</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Breakdowns: Funnel Stage + DQ'd By */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* By Funnel Stage */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">By Funnel Stage</CardTitle>
            <CardDescription>Last status before disqualification</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="animate-pulse space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-6 bg-muted rounded" />)}</div>
            ) : byStage.length > 0 ? (
              <div className="space-y-2">
                {byStage.map((s, i) => {
                  const pct = summary.total_dqs > 0 ? (s.count / summary.total_dqs) * 100 : 0
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span>{formatStatus(s.name)}</span>
                        <span className="font-medium">{s.count} ({pct.toFixed(0)}%)</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-destructive/70 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No data</p>
            )}
          </CardContent>
        </Card>

        {/* By User */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Disqualified By</CardTitle>
            <CardDescription>Who performed the disqualification</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="animate-pulse space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="h-6 bg-muted rounded" />)}</div>
            ) : byUser.length > 0 ? (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {byUser.map((u, i) => (
                  <div key={i} className="flex items-center justify-between gap-2">
                    <span className="text-sm truncate">{u.name}</span>
                    <span className="text-sm font-medium shrink-0">{u.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No data</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent DQs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent Disqualifications</CardTitle>
          <CardDescription>Up to 100 most recent in this period</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="animate-pulse space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-8 bg-muted rounded" />)}</div>
          ) : recent.length > 0 ? (
            <>
              {/* Mobile cards */}
              <div className="sm:hidden space-y-3">
                {recent.slice(0, 20).map((r, i) => (
                  <div key={i} className="border rounded-lg p-3 bg-muted/30">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{r.patient_first} {r.patient_last}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(r.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mb-1">{r.entity_id} · Order #{r.order_number}</div>
                    {r.disqualify_reason && <div className="text-sm text-red-600 dark:text-red-400">{r.disqualify_reason}</div>}
                    <div className="text-xs text-muted-foreground mt-1">
                      Stage: {formatStatus(r.last_status)}
                      {r.disqualified_by && ` · By: ${r.disqualified_by}`}
                    </div>
                  </div>
                ))}
                {recent.length > 20 && <p className="text-xs text-muted-foreground text-center">Showing 20 of {recent.length}</p>}
              </div>

              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-medium">Date</th>
                      <th className="text-left py-2 px-2 font-medium">Patient</th>
                      <th className="text-left py-2 px-2 font-medium">Product</th>
                      <th className="text-left py-2 px-2 font-medium">Reason</th>
                      <th className="text-left py-2 px-2 font-medium">Last Stage</th>
                      <th className="text-left py-2 px-2 font-medium">By</th>
                      <th className="text-right py-2 px-2 font-medium">Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((r, i) => (
                      <tr key={i} className="border-b border-muted/50 hover:bg-muted/50">
                        <td className="py-2 px-2 whitespace-nowrap">
                          {new Date(r.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </td>
                        <td className="py-2 px-2">
                          <div>{r.patient_first} {r.patient_last}</div>
                          <div className="text-xs text-muted-foreground">{r.patient_email}</div>
                        </td>
                        <td className="py-2 px-2">
                          <div>{r.entity_id}</div>
                          <div className="text-xs text-muted-foreground">#{r.order_number}</div>
                        </td>
                        <td className="py-2 px-2 max-w-[200px]">
                          {r.disqualify_reason ? (
                            <span className="text-red-600 dark:text-red-400">{r.disqualify_reason}</span>
                          ) : (
                            <span className="text-muted-foreground italic">Not specified</span>
                          )}
                        </td>
                        <td className="py-2 px-2 whitespace-nowrap">{formatStatus(r.last_stage)}</td>
                        <td className="py-2 px-2 text-xs">{r.disqualified_by || <span className="text-muted-foreground">—</span>}</td>
                        <td className="text-right py-2 px-2">
                          {r.payment_amount ? formatCurrency(r.payment_amount) : <span className="text-muted-foreground">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No disqualifications in this period</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
